import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Rocket, 
  Brain, 
  Users, 
  Target,
  Zap,
  Globe,
  Shield,
  Heart,
  Search,
  CheckCircle,
  Clock,
  Sparkles,
  Building,
  Github,
  Linkedin,
  Twitter,
  MapPin,
  Mail
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';

export default function AboutPage() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background border-b">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
        <div className="relative container px-4 md:px-6 flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] gap-4 py-12 text-center">
          <Badge variant="outline" className="mb-2">About NewLoop</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl mb-4">
            Building the Future of{' '}
            <span className="text-primary">Startup Hiring</span>
          </h1>
          <p className="max-w-[800px] text-muted-foreground md:text-xl dark:text-gray-400 mb-5">
            At NewLoop, we believe that hiring shouldn't slow down innovation. 
            Startups and high-growth companies need the right talent, at the right time. 
            Professionals deserve opportunities that fit who they are, not just what's on their resume.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
            <Button asChild size="lg" className="text-base">
              <Link to="/employers">
                For Employers
                <Building className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-base">
              <Link to="/professionals">
                For Professionals
                <Users className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 md:px-6 bg-muted/50">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Our Mission</Badge>
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-4">
                Hiring should never be the reason a startup stalls
              </h2>
              <p className="text-muted-foreground text-lg mb-6">
                We're combining AI-driven hiring intelligence, automated workflows, and deep recruiting expertise 
                to help startups scale their teams—without the bottlenecks of traditional hiring.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 mt-1 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">AI-powered precision</h3>
                    <p className="text-muted-foreground">
                      Intelligent matchmaking based on skills, experience, and team dynamics
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 mt-1 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Human-first approach</h3>
                    <p className="text-muted-foreground">
                      We don't just fill jobs. We build careers and long-term partnerships
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 mt-1 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Faster, fairer hiring</h3>
                    <p className="text-muted-foreground">
                      Speed up hiring without sacrificing quality or diversity
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl -rotate-6 scale-95 opacity-50" />
              <div className="relative bg-card border rounded-3xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Target className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">For Employers</h4>
                      <p className="text-sm text-muted-foreground">Make critical hires in weeks, not months</p>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">For Candidates</h4>
                      <p className="text-sm text-muted-foreground">Find opportunities that match your skills and values</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 md:px-6">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4">Leadership</Badge>
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">Meet the Founder</h2>
            </div>

            <Card className="overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <img
                    src="https://gexuxqaunwsfohpxacrp.supabase.co/storage/v1/object/public/marketing/Headshot.jpeg"
                    alt="Jonathan Daniels"
                    className="w-32 h-32 rounded-full object-cover border-2 border-border"
                  />
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-2xl font-bold">Jonathan Daniels</h3>
                        <p className="text-primary font-medium">Founder & CEO</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <a href="https://linkedin.com/in/jonathanrdaniels" target="_blank" rel="noopener noreferrer">
                            <Linkedin className="h-4 w-4 mr-2" />
                            LinkedIn
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href="https://github.com/J0nrages" target="_blank" rel="noopener noreferrer">
                            <Github className="h-4 w-4 mr-2" />
                            GitHub
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href="https://twitter.com/jondaniels_ai" target="_blank" rel="noopener noreferrer">
                            <Twitter className="h-4 w-4 mr-2" />
                            Twitter
                          </a>
                        </Button>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Jonathan is a recruiting and AI expert with over 6 years of experience in talent strategy, 
                      executive search, and startup hiring. Having worked with high-growth startups and scaling 
                      companies, he saw firsthand how broken the hiring process is—relying on biased filters, 
                      outdated resumes, and slow, inefficient systems.
                    </p>
                    <p className="text-muted-foreground mb-4">
                      That's why he built NewLoop—to fix hiring at its core. Jonathan believes hiring should be 
                      transparent, ethical, and empowering—for both candidates and companies. His expertise combines 
                      data-driven decision-making, deep recruiting knowledge, and cutting-edge AI, making him uniquely 
                      positioned to lead this transformation.
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>Austin, Texas</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <a href="mailto:jonathan@newloop.ai" className="hover:text-primary">
                          jonathan@newloop.ai
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">Our Values</h2>
            <p className="mt-4 text-primary-foreground/80 md:text-lg">
              These principles guide everything we do, from how we build our platform to how we support candidates and employers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Empowerment</h3>
              <p className="text-primary-foreground/80">
                We believe everyone deserves access to opportunities that align with their skills, values, and ambitions
              </p>
            </div>
            <div className="text-center">
              <div className="bg-white/10 w-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Transparency & Ethics</h3>
              <p className="text-primary-foreground/80">
                Our algorithms are designed to be explainable, fair, and free from bias
              </p>
            </div>
            <div className="text-center">
              <div className="bg-white/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Inclusivity & Fairness</h3>
              <p className="text-primary-foreground/80">
                We're committed to bridging gaps and ensuring equal access to opportunities
              </p>
            </div>
            <div className="text-center">
              <div className="bg-white/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Innovation with Purpose</h3>
              <p className="text-primary-foreground/80">
                We build solutions that matter, making hiring better, faster, and more human
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-20 px-4 md:px-6">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-4">
              Join Us in Transforming Hiring
            </h2>
            <p className="text-muted-foreground md:text-lg mb-8">
              Whether you're a startup looking to scale or a professional seeking your next opportunity, 
              we're here to help you succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/register">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact">
                  Contact Us
                  <Mail className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}